import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';
import { REPORT_TYPES, REPORT_ORDER, getReportType, buildFileUrl } from './reportTypes';

const getAuthHeaders = () => {
  const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const CARD_ICONS = {
  notice: '📢',
  annual: '📊',
  tender: '📄',
};

const CARD_COLORS = {
  notice: 'primary',
  annual: 'success',
  tender: 'warning',
};

const FileCell = ({ filePath }) => {
  const url = buildFileUrl(filePath);
  if (!url) return <span className="text-muted">—</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
      View File
    </a>
  );
};

const ReportList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeType = getReportType(searchParams.get('type')).key;
  const config = getReportType(activeType);

  const [counts, setCounts] = useState({ notice: 0, annual: 0, tender: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCounts = async () => {
    try {
      const results = await Promise.all(
        REPORT_ORDER.map((key) =>
          getRequest(`${REPORT_TYPES[key].endpoint}?limit=1`, getAuthHeaders())
        )
      );
      const next = {};
      REPORT_ORDER.forEach((key, idx) => {
        next[key] = results[idx]?.data?.total || 0;
      });
      setCounts(next);
    } catch {
      // silent
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await getRequest(`${config.endpoint}?limit=500`, getAuthHeaders());
      if (res?.data?.success) {
        setItems(res.data.data || []);
      } else {
        toast.error(res?.data?.message || `Failed to load ${config.label.toLowerCase()}s`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || `Error fetching ${config.label.toLowerCase()}s`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType]);

  const setActiveType = (key) => {
    setSearchParams({ type: key });
  };

  const handleToggleStatus = async (item) => {
    const newStatus = item.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await patchRequest(
        `${config.endpoint}/${item.id}/status`,
        { status: newStatus },
        getAuthHeaders()
      );
      if (res?.data?.success) {
        toast.success(`${config.label} ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchList();
        fetchCounts();
      } else {
        toast.error(res?.data?.message || 'Failed to update status');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error updating status');
    }
  };

  const handleDelete = async (item) => {
    const label = item[config.titleField] || item[config.nameField] || item.tender_title || item.name || item.report_title || `#${item.id}`;
    if (!window.confirm(`Are you sure you want to delete "${label}"?`)) return;
    try {
      const res = await deleteRequest(`${config.endpoint}/${item.id}`, getAuthHeaders());
      if (res?.data?.success) {
        toast.success(`${config.label} deleted successfully`);
        fetchList();
        fetchCounts();
      } else {
        toast.error(res?.data?.message || 'Delete failed');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting');
    }
  };

  const buildColumns = () => {
    const common = [
      { Header: 'ID', accessor: 'id' },
      {
        Header: 'File',
        accessor: 'file_path',
        Cell: ({ row }) => <FileCell filePath={row.original.file_path} />,
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ row }) => (
          <span className={`badge ${row.original.status === 'active' ? 'badge-success' : row.original.status === 'closed' ? 'badge-secondary' : 'badge-danger'}`}>
            {row.original.status}
          </span>
        ),
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-info mr-2"
              onClick={() => navigate(`/reports/update/${activeType}/${row.original.id}`)}
            >
              Edit
            </button>
            <button
              className={`btn btn-sm mr-2 ${row.original.status === 'active' ? 'btn-warning' : 'btn-success'}`}
              onClick={() => handleToggleStatus(row.original)}
            >
              {row.original.status === 'active' ? 'Deactivate' : 'Activate'}
            </button>
            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(row.original)}>
              Delete
            </button>
          </div>
        ),
      },
    ];

    if (activeType === 'notice') {
      return [
        { Header: 'ID', accessor: 'id' },
        { Header: 'Name', accessor: 'name' },
        { Header: 'Date', accessor: 'date' },
        { Header: 'Type', accessor: 'notice_type' },
        ...common.filter((c) => c.accessor !== 'id'),
      ];
    }

    if (activeType === 'annual') {
      return [
        { Header: 'ID', accessor: 'id' },
        { Header: 'Report Title', accessor: 'report_title' },
        { Header: 'Publication Date', accessor: 'publication_date' },
        ...common.filter((c) => c.accessor !== 'id'),
      ];
    }

    return [
      { Header: 'ID', accessor: 'id' },
      { Header: 'Title', accessor: 'tender_title' },
      { Header: 'Published Date', accessor: 'published_date' },
      {
        Header: 'Close Date',
        accessor: 'sale_close_date',
        Cell: ({ row }) => row.original.sale_close_date || <span className="text-muted">—</span>,
      },
      {
        Header: 'Description',
        accessor: 'tender_description',
        Cell: ({ row }) =>
          row.original.tender_description ? (
            <span>
              {String(row.original.tender_description).length > 50
                ? `${String(row.original.tender_description).slice(0, 50)}...`
                : row.original.tender_description}
            </span>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      ...common.filter((c) => c.accessor !== 'id'),
    ];
  };

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4 d-flex justify-content-between align-items-center">
        <h4 className="font-weight-bold">All Reports</h4>
        <button className="btn btn-success" onClick={() => navigate(`/reports/add?type=${activeType}`)}>
          + Add {config.label}
        </button>
      </div>

      <div className="row mx-2 mt-4">
        {REPORT_ORDER.map((key) => {
          const type = REPORT_TYPES[key];
          const isActive = key === activeType;
          return (
            <div key={key} className="col-md-4 col-sm-6 mb-3">
              <button
                type="button"
                className={`btn w-100 text-left shadow-sm ${isActive ? `btn-${CARD_COLORS[key]}` : 'btn-light'}`}
                style={{ borderRadius: '0.5rem', padding: '1.25rem' }}
                onClick={() => setActiveType(key)}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                      {CARD_ICONS[key]} {counts[key]}
                    </div>
                    <div className="small text-uppercase" style={{ opacity: 0.85 }}>
                      {type.label}s
                    </div>
                  </div>
                  <span className="small" style={{ opacity: 0.7 }}>
                    {isActive ? '●' : '○'}
                  </span>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      <div className="mx-4 mt-3">
        <h6 className="font-weight-bold mb-3">{config.label} List</h6>
        {loading ? (
          <div className="text-center mt-5">Loading {config.label.toLowerCase()}s...</div>
        ) : items.length > 0 ? (
          <AdminTable tableHeaders={buildColumns()} tableData={items} />
        ) : (
          <div className="text-center mt-5 text-white">No {config.label.toLowerCase()}s available</div>
        )}
      </div>
    </div>
  );
};

export default ReportList;
